import { difference } from 'es-toolkit';

import { positiveQuantityOrNull, ReadFile } from '@paragon/client-tools';
import { Station, SynchronizedResult } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { componentDesignsDeleted } from '@paragon/layout-models';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';

import { hasNextComponentDesign, hasPreviousComponentDesign } from '../selectors/componentNavigationSelectors';
import { getActiveComponentDesign } from '../selectors/getActiveComponentDesignSelector';
import { getProductionComponentInstanceByGuid } from '../selectors/getProductionComponentInstanceByGuid';
import { getSingleSelectedItemEntryOrNull } from '../selectors/getSelectedItemSelector';
import { loadStation } from '../slices/stationsSlice';
import { loadComponentDesign } from './loadActions';
import { forceClearSelection, moveToNextComponent, moveToPreviousComponent, selectItem } from './selectActions';
import { AsyncThunkActionVoid } from './thunkActions';

export const addComponentDesignsToActiveStation =
  (
    componentDesignGuids: string[],
    countFile?: ReadFile,
    quantitiesFromFiles?: { [componentDesignGuid: string]: number },
  ): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const { application } = getState();
    if (application.openContext?.type !== 'station' || componentDesignGuids.length === 0) {
      return;
    }

    const { stationGuid } = application.openContext;
    let makeFinalRequest: () => Promise<SynchronizedResult<Station>>;

    const useQuantitiesFromFiles = !countFile && !!quantitiesFromFiles;

    if (countFile) {
      makeFinalRequest = () => designService.stations.postApplyCountFileToStation(stationGuid, countFile);
    } else {
      const lastComponentDesignGuid = componentDesignGuids.pop()!;
      makeFinalRequest = () =>
        designService.stations.addComponentDesignGuidToStation(
          stationGuid,
          lastComponentDesignGuid,
          useQuantitiesFromFiles ? positiveQuantityOrNull(quantitiesFromFiles[lastComponentDesignGuid]) : null,
        );
    }

    await Promise.all(
      componentDesignGuids.map((guid) =>
        designService.stations.addComponentDesignGuidToStation(
          stationGuid,
          guid,
          useQuantitiesFromFiles ? positiveQuantityOrNull(quantitiesFromFiles[guid]) : null,
        ),
      ),
    );

    // By waiting to make the final request after all other requests are finished, we can count on the station that it
    //   returns to accurately reflect the final state of the station.  Synchronization will probably have taken care of
    //   this already, but we'll go ahead and load it just in case.
    const finalResponse = await makeFinalRequest();
    await dispatch(loadStation({ station: finalResponse.result }));
  };

export const synchronizeLocalStationAfterServerUpdate =
  (
    stationWasActive: boolean,
    updatedStation: Station,
    oldComponentDesignGuids: string[],
    newComponentDesignGuids: string[],
  ): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const spinnerId = dispatch(addSpinnerMessage('Synchronizing station...'));

    try {
      let state = getState();

      const oldActiveComponentDesign = getActiveComponentDesign(state);

      const loadingPromises = newComponentDesignGuids
        .filter((guid) => !(guid in state.componentDesigns.data) && !(guid in state.asyncRequests))
        .map((componentDesignGuid) =>
          dispatch(
            loadComponentDesign(componentDesignGuid, {
              setAnalysisSetAsActive: false,
              skipJackGirderConnections: false,
            }),
          ),
        );

      await Promise.all(loadingPromises);

      state = getState();
      if (newComponentDesignGuids.length > 0) {
        const firstLoadedComponentDesign = state.componentDesigns.data[newComponentDesignGuids[0]];

        if (getActiveComponentDesign(state) == null && firstLoadedComponentDesign != null) {
          dispatch(selectItem(firstLoadedComponentDesign, 'componentDesign'));
        }
      }

      if (stationWasActive) {
        const deletedComponentDesignGuids = difference(oldComponentDesignGuids, newComponentDesignGuids);
        for (const deletedComponentDesignGuid of deletedComponentDesignGuids) {
          dispatch(componentDesignsDeleted(deletedComponentDesignGuid));
        }

        state = getState();

        if (oldActiveComponentDesign && deletedComponentDesignGuids.includes(oldActiveComponentDesign.guid)) {
          if (hasNextComponentDesign(state)) {
            dispatch(moveToNextComponent());
          } else if (hasPreviousComponentDesign(state)) {
            dispatch(moveToPreviousComponent());
          } else {
            dispatch(forceClearSelection());
          }
        }

        if (updatedStation.followsSchedule) {
          // The station has been updated, but we need to select the new component.
          const { currentProductionComponentInstanceGuid } = updatedStation;
          if (!currentProductionComponentInstanceGuid) return;
          const currentlySelectedItem = getSingleSelectedItemEntryOrNull(getState());
          if (!currentlySelectedItem || currentlySelectedItem.item.guid !== currentProductionComponentInstanceGuid) {
            // It is possible that the station is open in two tabs/computers and they are on different production
            // groups. In this case we do not have the instance loaded, so we do not want to try to select it. For now,
            // we leave it up to users to keep multiple machines looking at the same station on the same production
            // group instead of trying to do some magic where we load that production group and switch to it - a jump
            // that could surprise and frustrate the user. In most cases, it should be one machine per station anyway.
            const productionComponentInstanceByGuid = getProductionComponentInstanceByGuid(getState());
            if (productionComponentInstanceByGuid[currentProductionComponentInstanceGuid]) {
              // Calling `selectItem` has the potential to trigger a `station.currentProductionComponentInstanceGuid`
              // update, which is synchronized and could then call this again. This happens in cases of quick subsequent
              // selection changes, like mashing the spacebar.
              dispatch(
                selectItem(
                  productionComponentInstanceByGuid[currentProductionComponentInstanceGuid],
                  'productionComponentInstance',
                ),
              );
            }
          }
        }
      }
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
